import { AnchorHTMLAttributes, ReactNode, Ref } from 'react';
import { AnyRouter, createLink, LinkOptions, RegisteredRouter } from '@tanstack/react-router';
import { useIsRouteMatching } from '@/common/hooks/useIsRouteMatching';
import { cn } from '@/lib/utils';

const NavLinkComponent = (
  props: AnchorHTMLAttributes<HTMLAnchorElement> & { ref?: Ref<HTMLAnchorElement> }
) => <a {...props} />;

const NavLink = createLink(NavLinkComponent);

type NavMenuLinkProps<
  TRouter extends AnyRouter = RegisteredRouter,
  TFrom extends string = string,
  TTo extends string | undefined = '.',
  TMaskFrom extends string = TFrom,
  TMaskTo extends string = '.',
> = LinkOptions<TRouter, TFrom, TTo, TMaskFrom, TMaskTo> & {
  icon: ReactNode;
  label: ReactNode;
  variant?: 'default' | 'bottom';
  /** Use exact (non-fuzzy) matching for active detection. Required for the root "/" route. */
  exact?: boolean;
  ref?: Ref<HTMLAnchorElement>;
};

export const NavMenuLink = ({
  icon,
  label,
  variant = 'default',
  exact = false,
  ...rest
}: NavMenuLinkProps) => {
  const isActive = useIsRouteMatching({ to: rest.to, fuzzy: !exact });

  if (variant === 'bottom') {
    return (
      <NavLink
        {...rest}
        className={cn(
          'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 border',
          isActive
            ? 'bg-primary/10 text-primary border-primary/20'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground border-transparent'
        )}
      >
        {icon}
        <span>{label}</span>
      </NavLink>
    );
  }

  return (
    <NavLink
      {...rest}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200',
        isActive
          ? 'bg-card text-primary'
          : 'text-muted-foreground hover:bg-card hover:text-foreground'
      )}
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
};
